import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Trash2, Pencil, X, Check } from 'lucide-react'
import { api } from '@/lib/api/client'
import type { RateEntry, RateScope, PersonDirectoryEntry } from '@/types/api'

interface RatesTableProps {
  scope:    RateScope
  /** Required for project / phase / activity scopes; ignored for workspace. */
  parentId?: number
}

/** Reusable table for managing time-bounded rate entries at any scope. */
export default function RatesTable({ scope, parentId }: RatesTableProps) {
  const queryClient = useQueryClient()

  const path = scope === 'workspace'
    ? '/rates/workspace'
    : `/rates/${scope}/${parentId}`

  const qkey = ['rates', scope, parentId ?? null]

  const { data: entries = [], isLoading } = useQuery({
    queryKey: qkey,
    queryFn:  () => api.get<RateEntry[]>(path),
    enabled:  scope === 'workspace' || parentId != null,
  })

  const { data: people = [] } = useQuery({
    queryKey: ['people'],
    queryFn:  () => api.get<PersonDirectoryEntry[]>('/people'),
    staleTime: 5 * 60 * 1000,
  })

  const [adding, setAdding]       = useState(false)
  const [editingId, setEditingId] = useState<number | null>(null)

  const invalidate = () => queryClient.invalidateQueries({ queryKey: qkey })

  const createMutation = useMutation({
    mutationFn: (body: RateForm) => api.post(path, formToBody(body)),
    onSuccess:  () => { invalidate(); setAdding(false) },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, body }: { id: number; body: RateForm }) =>
      api.put(`/rates/${scope}/entry/${id}`, formToBody(body)),
    onSuccess: () => { invalidate(); setEditingId(null) },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: number) => api.delete(`/rates/${scope}/entry/${id}`),
    onSuccess:  () => invalidate(),
  })

  return (
    <div>
      <div style={{
        border:       '1px solid var(--ns-border)',
        borderRadius: 6,
        overflow:     'hidden',
        background:   'var(--ns-surface)',
      }}>
        <table className="table" style={{ margin: 0, fontSize: 12.5 }}>
          <thead>
            <tr>
              <th style={{ width: 160 }}>Applies to</th>
              <th style={{ width: 110, textAlign: 'right' }}>Rate (kr/h)</th>
              <th style={{ width: 120 }}>Valid from</th>
              <th style={{ width: 120 }}>Valid to</th>
              <th>Notes</th>
              <th style={{ width: 70 }} />
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr><td colSpan={6} className="muted" style={{ textAlign: 'center', padding: 16 }}>Loading…</td></tr>
            )}
            {!isLoading && entries.length === 0 && !adding && (
              <tr><td colSpan={6} className="muted" style={{ textAlign: 'center', padding: 16 }}>
                No rate entries — inherits from the level above.
              </td></tr>
            )}
            {entries.map(e =>
              editingId === e.id
                ? <RowEditor
                    key={e.id}
                    initial={entryToForm(e)}
                    people={people}
                    onCancel={() => setEditingId(null)}
                    onSave={body => updateMutation.mutate({ id: e.id, body })}
                    pending={updateMutation.isPending}
                  />
                : <Row
                    key={e.id}
                    entry={e}
                    onEdit={() => setEditingId(e.id)}
                    onDelete={() => {
                      if (confirm('Delete this rate entry?')) deleteMutation.mutate(e.id)
                    }}
                  />
            )}
            {adding && (
              <RowEditor
                initial={blankForm()}
                people={people}
                onCancel={() => setAdding(false)}
                onSave={body => createMutation.mutate(body)}
                pending={createMutation.isPending}
              />
            )}
          </tbody>
        </table>
      </div>

      {!adding && (
        <button
          className="btn btn-ghost"
          style={{ marginTop: 8, gap: 4, fontSize: 12.5 }}
          onClick={() => setAdding(true)}
          disabled={editingId !== null}
        >
          <Plus size={12} />Add rate entry
        </button>
      )}
    </div>
  )
}

// ─── Row in display mode ──────────────────────────────────────────────────────

function Row({ entry, onEdit, onDelete }: {
  entry:    RateEntry
  onEdit:   () => void
  onDelete: () => void
}) {
  return (
    <tr>
      <td>{entry.personName ?? <span className="muted">All people</span>}</td>
      <td className="mono tabular" style={{ textAlign: 'right' }}>
        {entry.hourlyRate.toLocaleString('sv-SE')}
      </td>
      <td className="mono" style={{ fontSize: 12 }}>{entry.validFrom}</td>
      <td className="mono" style={{ fontSize: 12 }}>{entry.validTo ?? <span className="muted">∞</span>}</td>
      <td className="muted" style={{ fontSize: 12 }}>{entry.notes ?? ''}</td>
      <td>
        <div className="row" style={{ gap: 2, justifyContent: 'flex-end' }}>
          <button className="btn btn-ghost btn-icon" title="Edit" onClick={onEdit}>
            <Pencil size={11} />
          </button>
          <button
            className="btn btn-ghost btn-icon"
            title="Delete"
            style={{ color: 'var(--ns-err)' }}
            onClick={onDelete}
          >
            <Trash2 size={11} />
          </button>
        </div>
      </td>
    </tr>
  )
}

// ─── Inline editor row ────────────────────────────────────────────────────────

interface RateForm {
  hourlyRate: string
  validFrom:  string
  validTo:    string
  personId:   string
  notes:      string
}

function blankForm(): RateForm {
  const today = new Date().toISOString().slice(0, 10)
  return { hourlyRate: '', validFrom: today, validTo: '', personId: '', notes: '' }
}

function entryToForm(e: RateEntry): RateForm {
  return {
    hourlyRate: String(e.hourlyRate),
    validFrom:  e.validFrom,
    validTo:    e.validTo ?? '',
    personId:   e.personId != null ? String(e.personId) : '',
    notes:      e.notes ?? '',
  }
}

function formToBody(f: RateForm) {
  return {
    personId:   f.personId ? Number(f.personId) : null,
    hourlyRate: Number(f.hourlyRate),
    validFrom:  f.validFrom,
    validTo:    f.validTo || null,
    notes:      f.notes || null,
  }
}

function RowEditor({
  initial, people, onSave, onCancel, pending,
}: {
  initial:  RateForm
  people:   PersonDirectoryEntry[]
  onSave:   (body: RateForm) => void
  onCancel: () => void
  pending:  boolean
}) {
  const [form, setForm] = useState<RateForm>(initial)

  const valid =
    form.hourlyRate !== '' &&
    !isNaN(Number(form.hourlyRate)) &&
    Number(form.hourlyRate) >= 0 &&
    !!form.validFrom

  return (
    <tr style={{ background: 'var(--ns-surface-2)' }}>
      <td>
        <select
          className="input"
          value={form.personId}
          onChange={e => setForm(s => ({ ...s, personId: e.target.value }))}
          style={{ width: '100%', fontSize: 12 }}
        >
          <option value="">All people</option>
          {people.filter(p => p.isActive).map(p => (
            <option key={p.id} value={p.id}>{p.firstName} {p.lastName}</option>
          ))}
        </select>
      </td>
      <td>
        <input
          className="input mono"
          type="number"
          min="0"
          step="50"
          value={form.hourlyRate}
          onChange={e => setForm(s => ({ ...s, hourlyRate: e.target.value }))}
          placeholder="0"
          style={{ width: '100%', textAlign: 'right', fontSize: 12 }}
        />
      </td>
      <td>
        <input
          className="input mono"
          type="date"
          value={form.validFrom}
          onChange={e => setForm(s => ({ ...s, validFrom: e.target.value }))}
          style={{ width: '100%', fontSize: 12 }}
        />
      </td>
      <td>
        <input
          className="input mono"
          type="date"
          value={form.validTo}
          onChange={e => setForm(s => ({ ...s, validTo: e.target.value }))}
          style={{ width: '100%', fontSize: 12 }}
        />
      </td>
      <td>
        <input
          className="input"
          value={form.notes}
          onChange={e => setForm(s => ({ ...s, notes: e.target.value }))}
          placeholder="Optional"
          style={{ width: '100%', fontSize: 12 }}
        />
      </td>
      <td>
        <div className="row" style={{ gap: 2, justifyContent: 'flex-end' }}>
          <button
            className="btn btn-ghost btn-icon"
            title="Save"
            disabled={!valid || pending}
            onClick={() => onSave(form)}
            style={{ color: 'var(--ns-ok)' }}
          >
            <Check size={12} />
          </button>
          <button
            className="btn btn-ghost btn-icon"
            title="Cancel"
            onClick={onCancel}
            disabled={pending}
          >
            <X size={12} />
          </button>
        </div>
      </td>
    </tr>
  )
}
